import * as React from 'react';
import {promiseDebounce} from './utils/debounce';
import {SearchAction, Search as tSearch, SearchResult} from 'priority-web-sdk/enums';
import {
    searchToResults,
    calculateSelectedIndex,
    calculateFocusedIndex,
    sortResults,
    Direction
} from './utils/search';
import {getNextSortDirection, getNextSortBy} from './utils/sortOptions';

import {spreadSheetProps} from '../../utils/paste';

const PAGE_SIZE = 5;

interface SearchState {
    results: SearchResult[];
    title1?: string;
    title2?: string;
    searchQuery: string;
    searchByTitle?: string;
    ignorecase?: boolean;
    searchFromBeginning?: boolean;
    isSearchable: boolean;
    isScrollable: boolean;
    isLoading: boolean;
    isSearching: boolean;
    isScrolling: boolean;
    isNoResults: boolean;
    isZoom: boolean;
    sortBy?: 'string1' | 'string2';
    isDesc?: boolean;
    selectedIndex: number;
    focusedIndex: number;
    chooseid?: string;
    inputValue?: string;
    direction: Direction;
}

export interface SearchProps {
    searchQuery: string;
    onSearch(searchAction: SearchAction, searchQuery: string): Promise<tSearch>;
    onSearchNext(page: number, searchQuery: string): Promise<tSearch>;
    render(p: InjectedSearchProps): JSX.Element;
    addValue(value: string): void;
    onClose(): void;
}

export interface InjectedSearchProps extends SearchState {
    handleSearchChange(value: string, isSkipCallback?: boolean): void;
    handleScroll(selectedIndex: number): Promise<any>;
    /**
     *
     * @param searchQuery
     * @param ignoreSearchQuery - is passed to mark the cases when there's a searchQuery different from emprtString,
     * but in case it will be Choose with two columns (with title2) we won't do client side filtering
     * @param isSearch - is true if we want the field to be opened as Search no matter what
     */
    handleOpen(
        callback: (query?: string) => Promise<any>,
        searchQuery: string,
        ignoreSearchQuery?: boolean
    ): Promise<any>;
    handleIgnoreCaseChange(): void;
    handleSearchFromBeginningChange(): void;
    handleSearchByChange(): void;
    handleSortByChange(sortBy: 'string1' | 'string2'): void;
    handlePageDown(selectedIndex: number): Promise<any>;
    handlePageUp(selectedIndex: number): number;
    handleSpreadsheetValues?: (parsedSpreadSheet: spreadSheetProps) => void;
}

export class Search extends React.Component<SearchProps, SearchState> {
    initState = {
        results: [],
        isSearchable: false,
        isScrollable: false,
        isLoading: false,
        isSearching: false,
        isScrolling: false,
        isNoResults: false
    };

    _results = [];
    onSearch;
    pageInd = 0;

    constructor(props) {
        super(props);
        this.state = {
            ...this.initState,
            searchQuery: '',
            isZoom: false,
            isLoading: true,
            selectedIndex: -1,
            focusedIndex: 0,
            direction: 'auto'
        };

        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleOpen = this.handleOpen.bind(this);
        this.handleIgnoreCaseChange = this.handleIgnoreCaseChange.bind(this);
        this.handleSearchFromBeginningChange = this.handleSearchFromBeginningChange.bind(this);
        this.handleSearchByChange = this.handleSearchByChange.bind(this);
        this.onSearch = promiseDebounce(this.props.onSearch, 400);
    }

    handleOpen(callback, searchQuery = '', ignoreSearchQuery = false) {
        this.pageInd = 0;
        return new Promise((resolve, reject) => {
            this.setState({isLoading: true, searchQuery}, () => {
                callback(searchQuery)
                    .then(result => {
                        this.processOpenResults(searchQuery, result, ignoreSearchQuery);
                        resolve({
                            isEmpty: !result.SearchLine && !result.ChooseLine,
                            isZoom: result.zoom,
                            isSearchResults: result.SearchLine,
                            multi: result.multi || result.ismulti,
                            isOneColumnChoose: result.title1 && !result.title2
                        });
                    })
                    .catch(() => reject());
            });
        });
    }

    processOpenResults(searchQuery, result, isArrowOpen) {
        const {
            isSearchable,
            results,
            isScrollable,
            isZoom,
            sortBy,
            isDesc,
            inputValue,
            direction,
            ...rest
        } = searchToResults(result);
        if (results.length) {
            this._results = [...results];
            const sortedResults = sortBy ? sortResults(results, sortBy, isDesc) : results;
            let filteredResults;
            if (isSearchable || !searchQuery || !rest.title2 || isArrowOpen) {
                filteredResults = sortedResults;
            } else {
                filteredResults = sortedResults.filter(({string1, string2}) => {
                    return (
                        (string1 && string1.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (string2 && string2.toLowerCase().includes(searchQuery.toLowerCase()))
                    );
                });
            }
            const arrowOpenQuery = isSearchable && inputValue ? inputValue : searchQuery;
            const selectedIndex = isArrowOpen
                ? calculateSelectedIndex(filteredResults, searchQuery)
                : -1;
            const focusedIndex = calculateFocusedIndex(filteredResults, searchQuery, isSearchable);
            this.setState(() => ({
                isSearchable,
                isScrollable,
                results: filteredResults,
                isLoading: false,
                isNoResults: false,
                sortBy,
                isDesc,
                searchQuery: isArrowOpen ? arrowOpenQuery : this.state.searchQuery,
                selectedIndex,
                focusedIndex,
                direction: direction,
                ...rest
            }));
        } else {
            this.setState(() => ({
                ...this.initState,
                isZoom,
                isSearchable,
                isNoResults: true,
                ...rest
            }));
        }
    }

    handleSpreadsheetValues = (parsedSpreadSheet: spreadSheetProps) => {
        const {values} = parsedSpreadSheet;
        const {addValue} = this.props;
        values.forEach((value: string) => {
            addValue(value);
        });
    };

    handleSearchChange(searchQuery, isSkipCallback = false) {
        const {title2} = this.state;
        this.setState({searchQuery: searchQuery, focusedIndex: -1, selectedIndex: -1}, () => {
            if (isSkipCallback) {
                return;
            }
            if (this.state.isSearchable) {
                this.setState({isSearching: true}, () => {
                    this.pageInd = 0;
                    this.onSearch(SearchAction.TextChange, searchQuery).then(result => {
                        const {results, isScrollable} = searchToResults(result);
                        this.setState({
                            results,
                            isSearching: false,
                            isScrollable,
                            focusedIndex: calculateFocusedIndex(
                                results,
                                searchQuery,
                                this.state.isSearchable
                            )
                        });
                    });
                    // TODO: Add error handling
                });
            }
            // perform client side search
            else if (title2) {
                const filteredResults = this._results.filter(({string1, string2}) => {
                    return (
                        (string1 && string1.toLowerCase().includes(searchQuery.toLowerCase())) ||
                        (string2 && string2.toLowerCase().includes(searchQuery.toLowerCase()))
                    );
                });

                const focusedIndex = calculateFocusedIndex(
                    filteredResults,
                    searchQuery,
                    this.state.isSearchable
                );

                this.setState({
                    results: filteredResults,
                    focusedIndex
                });
            } else {
                const focusedIndex = calculateFocusedIndex(
                    this._results,
                    searchQuery,
                    this.state.isSearchable
                );
                this.setState({
                    focusedIndex
                });
            }
        });
    }

    handleScroll() {
        const {onSearchNext} = this.props;
        if (this.state.isScrollable && !this.state.isScrolling) {
            return new Promise(resolve => {
                this.setState({isScrolling: true}, () => {
                    onSearchNext(++this.pageInd, this.state.searchQuery).then(result => {
                        const {results, isScrollable} = searchToResults(result);
                        const nextResults = [];

                        results.forEach(result => {
                            result.retval && nextResults.push(result);
                        });
                        this.setState(({results}) => ({
                            results: results.concat(nextResults),
                            isScrollable,
                            isScrolling: false
                        }));

                        // save the results for further client side searching (when the field is not searchable)
                        this._results = this._results.concat(nextResults);
                        resolve(undefined);
                    });
                });
            });
        } else {
            return Promise.resolve();
        }
    }

    handleIgnoreCaseChange() {
        if (this.state.isSearchable && !this.state.isSearching) {
            this.setState({isSearching: true, ignorecase: !this.state.ignorecase}, () => {
                this.pageInd = 0;
                this.onSearch(SearchAction.IgnoreCaseChanged, this.state.searchQuery).then(
                    result => {
                        const {results, ignorecase, isScrollable} = searchToResults(result);
                        const nextResults = [];

                        results.forEach(result => {
                            result.retval && nextResults.push(result);
                        });

                        this.setState({
                            results: nextResults,
                            ignorecase,
                            isScrollable,
                            isSearching: false,
                            focusedIndex: calculateFocusedIndex(
                                nextResults,
                                this.state.searchQuery,
                                this.state.isSearchable
                            )
                        });

                        // save the results for further client side searching (when the field is not searchable)
                        this._results = nextResults;
                    }
                );
            });
        }
    }

    handleSearchFromBeginningChange() {
        if (this.state.isSearchable && !this.state.isSearching) {
            this.setState(
                {isSearching: true, searchFromBeginning: !this.state.searchFromBeginning},
                () => {
                    this.pageInd = 0;
                    this.onSearch(SearchAction.StartChange, this.state.searchQuery).then(result => {
                        const {results, searchFromBeginning, isScrollable} =
                            searchToResults(result);
                        const nextResults = [];

                        results.forEach(result => {
                            result.retval && nextResults.push(result);
                        });

                        this.setState({
                            results: nextResults,
                            searchFromBeginning,
                            isScrollable,
                            isSearching: false,
                            focusedIndex: calculateFocusedIndex(
                                nextResults,
                                this.state.searchQuery,
                                this.state.isSearchable
                            )
                        });

                        // save the results for further client side searching (when the field is not searchable)
                        this._results = nextResults;
                    });
                }
            );
        }
    }

    handleSearchByChange() {
        if (this.state.isSearchable) {
            this.setState({isSearching: true}, () => {
                this.pageInd = 0;
                this.onSearch(SearchAction.TypeChange, this.state.searchQuery).then(result => {
                    const {results, searchByTitle, direction} = searchToResults(result);
                    const nextResults = [];

                    results.forEach(result => {
                        result.retval && nextResults.push(result);
                    });

                    this.setState({
                        results: nextResults,
                        searchByTitle,
                        isSearching: false,
                        direction: direction,
                        focusedIndex: calculateFocusedIndex(
                            nextResults,
                            this.state.searchQuery,
                            this.state.isSearchable
                        )
                    });

                    // save the results for further client side searching (when the field is not searchable)
                    this._results = nextResults;
                });
            });
        } else {
            if (this.state.title2) {
                this._results = this._results.map(result => {
                    if (result.string2) {
                        return {
                            string1: result.string2,
                            string2: result.string1,
                            retval: result.retval
                        };
                    }
                    return result;
                });

                this.setState(({searchQuery, title1, title2}) => {
                    const filteredResults = this._results.filter(({string1}) => {
                        return string1.toLowerCase().startsWith(searchQuery.toLowerCase());
                    });
                    return {
                        results: filteredResults,
                        title2: title1,
                        title1: title2,
                        focusedIndex: calculateFocusedIndex(
                            filteredResults,
                            this.state.searchQuery,
                            this.state.isSearchable
                        )
                    };
                });
            }
        }
    }

    handleSortByChange = sortByVal => {
        const {sortBy, isDesc, results} = this.state;
        const nextSortDir = getNextSortDirection(sortByVal, sortBy, isDesc);
        const sortByNext = getNextSortBy(sortByVal, sortBy, isDesc);

        const sortedRes = sortByNext
            ? sortResults(results, sortByNext, nextSortDir === 'desc')
            : [...this._results];
        this.setState(
            {results: sortedRes, sortBy: sortByNext, isDesc: nextSortDir === 'desc'},
            () => {}
        );
    };

    handlePageDown = selectedIndex => {
        const {isScrollable} = this.state;
        if (isScrollable) {
            if (selectedIndex + PAGE_SIZE < this.state.results.length) {
                return Promise.resolve({nextSelected: selectedIndex + PAGE_SIZE});
            } else {
                const prevResLength = this.state.results.length;
                return this.handleScroll().then(() => {
                    const nextIndex =
                        prevResLength + (selectedIndex % PAGE_SIZE) < this.state.results.length
                            ? prevResLength + (selectedIndex % PAGE_SIZE)
                            : this.state.results.length - 1;
                    return {nextSelected: nextIndex};
                });
            }
        } else {
            const nextIndex =
                selectedIndex + PAGE_SIZE < this.state.results.length
                    ? selectedIndex + PAGE_SIZE
                    : this.state.results.length - 1;
            return Promise.resolve({nextSelected: nextIndex});
        }
    };

    handlePageUp = selectedIndex => {
        return selectedIndex - PAGE_SIZE > 0 ? selectedIndex - PAGE_SIZE : 0;
    };

    render() {
        const {render: Component} = this.props;
        return (
            <Component
                {...this.props}
                {...this.state}
                handleOpen={this.handleOpen}
                handleScroll={this.handleScroll}
                handleSearchChange={this.handleSearchChange}
                handleIgnoreCaseChange={this.handleIgnoreCaseChange}
                handleSearchFromBeginningChange={this.handleSearchFromBeginningChange}
                handleSearchByChange={this.handleSearchByChange}
                handleSortByChange={this.handleSortByChange}
                handlePageDown={this.handlePageDown}
                handlePageUp={this.handlePageUp}
                handleSpreadsheetValues={this.handleSpreadsheetValues}
            />
        );
    }
}

export const withSearch = <P extends InjectedSearchProps>(Component: React.ComponentType<P>) => {
    return props => {
        return <Search {...props} render={p => <Component {...(p as P)} />} />;
    };
};
